import type { SourceProcessing } from "./domain";

/**
 * True when the counts describe work that is still moving — queued or in
 * progress.
 *
 * `failed` is deliberately not live work, but not for the reason this function
 * used to give. The old comment said the worker retries failed jobs on its own;
 * it does not. Every row behind this count has exhausted its attempts and been
 * dead-lettered, and a dead letter is never run again. Measured on the live
 * tenant on 2026-08-08: 1238 dead jobs while the footer said "nothing queued or
 * in progress" — which was true, and was read as "nothing is wrong".
 *
 * So live and stopped are two questions, and this one answers only the first.
 * {@link hasStoppedWork} answers the second, and the surface has to state both.
 */
export function hasLiveWork(counts: SourceProcessing): boolean {
  return counts.queued > 0 || counts.inProgress > 0;
}

/**
 * True when work has stopped and nothing will restart it on its own.
 *
 * The three states this separates are genuinely different sentences: work is
 * moving, work has stopped, or there is no work. Collapsing the middle one into
 * either of the others is what let a screen say the system was busy and idle in
 * the same breath while the mail lane had not advanced for thirteen days.
 */
export function hasStoppedWork(counts: SourceProcessing): boolean {
  return counts.failed > 0;
}

export function totalWork(counts: SourceProcessing): number {
  return counts.queued + counts.inProgress + counts.failed;
}

export function addWork(
  total: SourceProcessing,
  counts: SourceProcessing,
): SourceProcessing {
  return {
    queued: total.queued + counts.queued,
    inProgress: total.inProgress + counts.inProgress,
    failed: total.failed + counts.failed,
  };
}
